import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { Input as AntInput, InputNumber, Modal, Select as AntSelect, Checkbox, Button, Tooltip } from "antd";
import { Info } from "lucide-react";
import { IVariant, VariantFormValues } from "../../../types/api/product";
import UploadImage from "../../components/UploadImage";
import ApiSelect from "../../components/ApiSelect";
import ImageAltEditor from "../../components/ImageAltEditor";
import { useVariantCreate, useVariantUpdate } from "../../../hooks/api/variant/hooks";
import { useMasterList } from "../../../hooks/api/master/hooks";

const emptyValues: VariantFormValues = {
  sku: "",
  price: 0,
  artistPrice: 0,
  isPriceOnApplication: false,
  isForSale: true,
  currency: "GBP",
  editionText: "",
  stock: 1,
  isCommissionAvailable: false,
  isCommissioned: false,
  artistProofsAvailable: false,
  artistProofsCount: 0,
  images: [],
  imageAlts: {},
  materialId: null,
  finishId: null,
  methodId: null,
  baseId: null,
  dimensions: {
    height: 0,
    width: 0,
    depth: 0,
    unit: "cm",
  },
  weight: {
    value: 0,
    unit: "kg",
  },
  year: null,
  isDefault: false,
  status: true,
};

interface VariantModalProps {
  productId: string;
  isOpen: boolean;
  onClose: () => void;
  selectedItem?: IVariant | null;
  qData?: any;
}

const VariantModal: React.FC<VariantModalProps> = ({
  productId,
  isOpen,
  onClose,
  selectedItem,
  qData,
}) => {
  const { control, handleSubmit, reset, watch } = useForm<VariantFormValues>({
    defaultValues: emptyValues,
  });

  const isPOA = watch("isPriceOnApplication");
  const watchImages = watch("images");

  const createMutation = useVariantCreate({ qData, onSuccess: onClose });
  const updateMutation = useVariantUpdate({ qData, onSuccess: onClose });

  const { data: materialData, refetchWithQuery: refetchMaterial } = useMasterList(
    { limit: 100, page: 1, type: "MEDIUM" } as any,
    { enabled: isOpen }
  );
  const { data: finishData } = useMasterList(
    { limit: 100, page: 1, type: "FINISH" } as any,
    { enabled: isOpen }
  );
  const { data: methodData } = useMasterList(
    { limit: 100, page: 1, type: "METHOD" } as any,
    { enabled: isOpen }
  );
  const { data: baseData } = useMasterList(
    { limit: 100, page: 1, type: "BASE" } as any,
    { enabled: isOpen }
  );

  const buildOptions = (list: any[] | undefined, selected: any) => {
    const map = new Map<string, any>();
    if (selected && typeof selected === "object" && selected._id) {
      map.set(String(selected._id), { label: selected.name || "-", value: selected._id });
    }
    (list || []).forEach((item: any) => {
      if (item && item._id) map.set(String(item._id), { label: item.name, value: item._id });
    });
    return Array.from(map.values());
  };

  const materialOptions = React.useMemo(
    () => buildOptions(materialData?.data, selectedItem?.materialId),
    [materialData, selectedItem],
  );
  const finishOptions = React.useMemo(
    () => buildOptions(finishData?.data, selectedItem?.finishId),
    [finishData, selectedItem],
  );
  const methodOptions = React.useMemo(
    () => buildOptions(methodData?.data, selectedItem?.methodId),
    [methodData, selectedItem],
  );
  const baseOptions = React.useMemo(
    () => buildOptions(baseData?.data, selectedItem?.baseId),
    [baseData, selectedItem],
  );

  useEffect(() => {
    if (!isOpen) return;

    if (selectedItem) {
      const idOf = (val: any) => (typeof val === "object" ? val?._id || null : val ?? null);

      reset({
        sku: selectedItem.sku ?? "",
        price: selectedItem.price ?? 0,
        artistPrice: selectedItem.artistPrice ?? 0,
        isPriceOnApplication: selectedItem.isPriceOnApplication ?? false,
        isForSale: selectedItem.isForSale ?? true,
        currency: selectedItem.currency ?? "GBP",
        editionText: selectedItem.editionText ?? "",
        stock: selectedItem.stock ?? 1,
        isCommissionAvailable: selectedItem.isCommissionAvailable ?? false,
        isCommissioned: selectedItem.isCommissioned ?? false,
        artistProofsAvailable: selectedItem.artistProofsAvailable ?? false,
        artistProofsCount: selectedItem.artistProofsCount ?? 0,
        images: selectedItem.images ?? [],
        imageAlts: selectedItem.imageAlts ?? {},
        materialId: idOf(selectedItem.materialId),
        finishId: idOf(selectedItem.finishId),
        methodId: idOf(selectedItem.methodId),
        baseId: idOf(selectedItem.baseId),
        // Don't fabricate 0 for a genuinely-unset legacy value - same fix
        // applied to the Basic Info tab's default-edition fields.
        dimensions: {
          height: selectedItem.dimensions?.height,
          width: selectedItem.dimensions?.width,
          depth: selectedItem.dimensions?.depth,
          unit: selectedItem.dimensions?.unit ?? "cm",
        },
        weight: {
          value: selectedItem.weight?.value,
          unit: selectedItem.weight?.unit ?? "kg",
        },
        year: selectedItem.year ?? null,
        isDefault: selectedItem.isDefault ?? false,
        status: selectedItem.status ?? true,
      });
    } else {
      reset(emptyValues);
    }
  }, [isOpen, selectedItem, reset]);

  const onSubmit = (values: VariantFormValues) => {
    // A POA variant is deliberately priceless - zero out price so a
    // stale/disabled numeric value never gets saved alongside the flag.
    const payload = values.isPriceOnApplication ? { ...values, price: 0 } : values;
    if (selectedItem) {
      updateMutation.mutate({ id: selectedItem._id, data: { ...payload, productId } });
    } else {
      createMutation.mutate({ ...payload, productId });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      destroyOnClose
      title={selectedItem ? "Edit Edition" : "Add Edition"}
      width={700}
      style={{ top: 20 }}
      styles={{
        body: { maxHeight: "80vh", overflowY: "auto" },
      }}
      footer={
        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit(onSubmit)}
            loading={isPending}
          >
            Save Variant
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 mt-4">
        <div className="grid grid-cols-1 gap-4">
          <Controller
            name="sku"
            control={control}
            render={({ field, fieldState }) => (
              <div>
                <label className="block text-sm font-medium mb-1">SKU (optional)</label>
                <AntInput {...field} placeholder="Enter SKU - most artists don't use one" status={fieldState.error ? "error" : ""} />
                {fieldState.error && <p className="text-red-500 text-xs mt-1">{fieldState.error.message}</p>}
              </div>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="price"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Price</label>
                <InputNumber
                  {...field}
                  className="w-full"
                  min={0}
                  disabled={isPOA}
                  placeholder={isPOA ? "POA" : undefined}
                />
              </div>
            )}
          />
          <Controller
            name="stock"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                  Number of Editions Available
                  <Tooltip title="How many of this size/material edition are available to sell right now.">
                    <Info size={13} className="text-slate-400" />
                  </Tooltip>
                </label>
                <InputNumber {...field} className="w-full" min={0} />
              </div>
            )}
          />
        </div>

        <Controller
          name="isPriceOnApplication"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)}>
              Price on Application (POA) - hides the price and shows &quot;Price on Enquiry&quot; to customers
            </Checkbox>
          )}
        />

        <div className="grid grid-cols-3 gap-4">
          <Controller
            name="dimensions.height"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Height (cm)</label>
                <InputNumber {...field} className="w-full" min={0} />
              </div>
            )}
          />
          <Controller
            name="dimensions.width"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Width (cm)</label>
                <InputNumber {...field} className="w-full" min={0} />
              </div>
            )}
          />
          <Controller
            name="dimensions.depth"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Depth (cm)</label>
                <InputNumber {...field} className="w-full" min={0} />
              </div>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="weight.value"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Weight (kg)</label>
                <InputNumber {...field} className="w-full" min={0} />
              </div>
            )}
          />
          <Controller
            name="materialId"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Material (Medium)</label>
                <ApiSelect
                  {...field}
                  className="w-full"
                  placeholder="Select Material"
                  options={materialOptions}
                  refetchWithQuery={refetchMaterial}
                  baseQuery={{ limit: 100, page: 1, type: "MEDIUM" }}
                />
              </div>
            )}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Controller
            name="year"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Creation Year</label>
                <InputNumber
                  {...field}
                  className="w-full"
                  placeholder="e.g. 2026"
                  value={field.value ?? undefined}
                  onChange={(val) => field.onChange(val ?? null)}
                />
              </div>
            )}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Controller
            name="finishId"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Finish</label>
                <AntSelect
                  {...field}
                  allowClear
                  className="w-full"
                  placeholder="Select Finish"
                  options={finishOptions}
                  // This modal opens on top of the Editions modal (nested
                  // modal, antd z-index ~1200) - the default popup container
                  // (document.body) collides with index.css's hardcoded
                  // ".ant-select-dropdown { z-index: 1050 !important; }",
                  // which was only ever high enough for a single modal layer.
                  // The dropdown rendered with real options the whole time,
                  // just behind this modal's opaque body (Oliver, Oct 2026).
                  // Rendering the popup inside this field's own parent
                  // instead - same fix ApiSelect already uses for Material -
                  // keeps it in this modal's own stacking context.
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                />
              </div>
            )}
          />
          <Controller
            name="methodId"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Method</label>
                <AntSelect
                  {...field}
                  allowClear
                  className="w-full"
                  placeholder="Select Method"
                  options={methodOptions}
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                />
              </div>
            )}
          />
          <Controller
            name="baseId"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Base</label>
                <AntSelect
                  {...field}
                  allowClear
                  className="w-full"
                  placeholder="Select Base"
                  options={baseOptions}
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                />
              </div>
            )}
          />
        </div>

        <Controller
          name="images"
          control={control}
          render={({ field: { value, onChange } }) => (
            <div className="flex flex-col gap-1">
              <label className="block text-sm font-medium mb-1">Variant Images</label>
              <UploadImage
                value={value as unknown as string}
                count={5}
                listType="picture-card"
                onChange={(urls) => onChange(urls)}
              />
            </div>
          )}
        />
        <Controller
          name="imageAlts"
          control={control}
          render={({ field }) => (
            <ImageAltEditor
              images={watchImages || []}
              alts={field.value}
              onChange={field.onChange}
            />
          )}
        />

        <div className="grid grid-cols-3 gap-4 border-t pt-4">
          <Controller
            name="isDefault"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)}>
                Set as Default Variant
              </Checkbox>
            )}
          />
          <Controller
            name="isCommissionAvailable"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)}>
                Commission Available
              </Checkbox>
            )}
          />
          <Controller
            name="status"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)}>
                Active Status
              </Checkbox>
            )}
          />
        </div>
      </form>
    </Modal>
  );
};

export default VariantModal;
